import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'

interface StoreLike {
  getState: () => App.State
  subscribe: (listener: () => void) => () => void
}

function getLoyaltyAccountBenefit(
  state: App.State,
  benefitType: App.LoyaltyBenefitType,
): App.LoyaltyBenefit | undefined {
  return state.loyalty.account.data?.benefits?.find(benefit => benefit.code === benefitType)
}

export function isLoyaltyAccountSettled(state: App.State): boolean {
  if (!getIsLoyaltyEnabled(state)) return true

  const { data, fetching, error, initial } = state.loyalty.account
  if (data) return true
  if (fetching) return false
  if (error) return true
  if (initial) return false
  return true
}

/**
 * Resolves with the matching Société benefit once the loyalty account fetch has
 * settled, or `undefined` if the member does not have that benefit.
 * Pass an `AbortSignal` to unsubscribe if the caller unmounts mid-wait.
 */
function waitForLoyaltyBenefit(
  store: StoreLike,
  benefitType: App.LoyaltyBenefitType,
  signal?: AbortSignal,
): Promise<App.LoyaltyBenefit | undefined> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(undefined)
      return
    }

    let settled = false
    let unsubscribe = () => {}

    const finish = (value: App.LoyaltyBenefit | undefined) => {
      if (settled) return
      settled = true
      unsubscribe()
      signal?.removeEventListener('abort', onAbort)
      resolve(value)
    }

    const onAbort = () => finish(undefined)

    const check = (): boolean => {
      if (signal?.aborted) {
        finish(undefined)
        return true
      }

      const state = store.getState()
      const benefit = getLoyaltyAccountBenefit(state, benefitType)
      if (benefit) {
        finish(benefit)
        return true
      }
      if (isLoyaltyAccountSettled(state)) {
        finish(undefined)
        return true
      }
      return false
    }

    unsubscribe = store.subscribe(() => {
      check()
    })
    signal?.addEventListener('abort', onAbort)

    check()
  })
}

export default waitForLoyaltyBenefit
